import * as React from 'react';
import { 
    Grid, 
    Box, 
    Paper, 
    Typography, 
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Collapse,
    IconButton
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BasicCard from 'components/Card';
import DoughnutChart from 'components/Graphs/DoughnutChart';
import BarChart from 'components/Graphs/BarChart';
import { useGetDashboardData, useGetDailySummary } from 'services/hooks/rentals';
import Skeleton from '@mui/material/Skeleton';
import { currencyFormatter } from 'utilities/currencyFormatter';
import { styled } from '@mui/material/styles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    '&.MuiTableCell-head': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 'bold',
    },
    '&.MuiTableCell-body': {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
}));

export default function Dashboard() {
    const [isSidebar, setIsSidebar] = React.useState(true);
    const [rental, setRental] = React.useState('')
    const [open, setOpen] = React.useState(false);
    const [showLandlordsTable, setShowLandlordsTable] = React.useState(false);
    const {dashboardData, isLoadingDashboardData, errorLoadingDashboardData} = useGetDashboardData();
    const {dailySummary, isLoadingDailySummary, errorDailySummary} = useGetDailySummary();

    const isLoading = isLoadingDashboardData || isLoadingDailySummary;
    const hasError = errorLoadingDashboardData || errorDailySummary;

    return (
        <Box m="20px">
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Dashboard
            </Typography>
            {dailySummary?.date && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {new Date(dailySummary.date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                    })}
                </Typography>
            )}

            {isLoading ? (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Grid container spacing={3}>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                <Grid item xs={12} sm={6} md={3} key={i}>
                                    <Skeleton variant="rectangular" height={100} />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            ) : hasError ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="error" variant="h6">
                        Error loading dashboard data. Please try again later.
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    {dailySummary && (
                        <>
                            {/* Key Metrics - Compact Cards */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                                    <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>
                                        Daily Collections
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {currencyFormatter(dailySummary.daily_collections?.amount || 0)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                        {dailySummary.daily_collections?.count || 0} payments
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.main', color: 'white' }}>
                                    <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>
                                        Total Arrears
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {currencyFormatter(dailySummary.arrears?.total_outstanding ?? 0)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                        Outstanding
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.main', color: 'white' }}>
                                    <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>
                                        Landlords to Pay
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {currencyFormatter(dailySummary.landlords_to_pay?.total_amount ?? 0)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                        {dailySummary.landlords_to_pay?.count ?? 0} landlord{dailySummary.landlords_to_pay?.count !== 1 ? 's' : ''} to pay
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
                                    <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>
                                        Available Units
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {dailySummary.find_house_requests?.available_units || 0}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                        Units for rent
                                    </Typography>
                                </Paper>
                            </Grid>

                            {/* Revenue & Profits - Combined Compact View */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2.5 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Revenue & Profits
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">Today</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="primary">
                                                {currencyFormatter(dailySummary.current_revenue?.today || 0)}
                                            </Typography>
                                            <Typography variant="caption" color={dailySummary.profits?.today >= 0 ? 'success.main' : 'error.main'}>
                                                Profit: {currencyFormatter(dailySummary.profits?.today || 0)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">This Month</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="primary">
                                                {currencyFormatter(dailySummary.current_revenue?.this_month || 0)}
                                            </Typography>
                                            <Typography variant="caption" color={dailySummary.profits?.this_month >= 0 ? 'success.main' : 'error.main'}>
                                                Profit: {currencyFormatter(dailySummary.profits?.this_month || 0)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">This Year</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="primary">
                                                {currencyFormatter(dailySummary.current_revenue?.this_year || 0)}
                                            </Typography>
                                            <Typography variant="caption" color={dailySummary.profits?.this_year >= 0 ? 'success.main' : 'error.main'}>
                                                Profit: {currencyFormatter(dailySummary.profits?.this_year || 0)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* Summary Metrics - Compact */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2.5 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Overview
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="h5" fontWeight="bold" color="primary">
                                                {dailySummary.summary_metrics?.total_properties || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Properties
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="h5" fontWeight="bold" color="success.main">
                                                {dailySummary.summary_metrics?.total_active_tenants || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Tenants
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="h5" fontWeight="bold" color="info.main">
                                                {dailySummary.summary_metrics?.total_occupied_units || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Occupied
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="h5" fontWeight="bold" color="warning.main">
                                                {dailySummary.summary_metrics?.total_unoccupied_units || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Unoccupied
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* Landlords to Pay - Collapsible (count & total_amount from API) */}
                            {dailySummary.landlords_to_pay && (dailySummary.landlords_to_pay.count > 0 || (dailySummary.landlords_to_pay.list?.length ?? 0) > 0) && (
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                Landlords to Pay — {dailySummary.landlords_to_pay.count ?? dailySummary.landlords_to_pay.list?.length ?? 0} landlord{(dailySummary.landlords_to_pay.count ?? dailySummary.landlords_to_pay.list?.length ?? 0) !== 1 ? 's' : ''} · {currencyFormatter(dailySummary.landlords_to_pay.total_amount ?? 0)}
                                            </Typography>
                                            {dailySummary.landlords_to_pay.list?.length > 0 && (
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => setShowLandlordsTable(!showLandlordsTable)}
                                                >
                                                    {showLandlordsTable ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Collapse in={showLandlordsTable}>
                                            <TableContainer sx={{ mt: 2 }}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <StyledTableCell>Property</StyledTableCell>
                                                            <StyledTableCell>Landlord</StyledTableCell>
                                                            <StyledTableCell>Amount</StyledTableCell>
                                                            <StyledTableCell>Status</StyledTableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {(dailySummary.landlords_to_pay.list ?? []).slice(0, 10).map((payment, idx) => (
                                                            <StyledTableRow key={payment.id ?? idx}>
                                                                <TableCell>{payment.property_name ?? '—'}</TableCell>
                                                                <TableCell>{payment.landlord_name ?? '—'}</TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {currencyFormatter(payment.amount ?? 0)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={payment.requested ? 'Requested' : 'Pending'}
                                                                        color={payment.requested ? 'warning' : 'default'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                            </StyledTableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Collapse>
                                    </Paper>
                                </Grid>
                            )}

                            {/* Charts Section */}
                            <Grid item xs={12} md={7}>
                                <Paper sx={{ p: 2.5 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Rent Payments Per Month
                                    </Typography>
                                    <BarChart/>
                                </Paper>   
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Paper sx={{ p: 2.5 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Rent Payments
                                    </Typography>
                                    <DoughnutChart/>
                                </Paper>                               
                            </Grid>
                        </>
                    )}

                    {/* Fallback to old dashboard if daily summary not available */}
                    {!dailySummary && dashboardData && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <BasicCard name={"LANDLORD PROPERTIES"} value={dashboardData.properties || 0}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <BasicCard name={"TOTAL COLLECTED (UGX)"} value={new Intl.NumberFormat('en-US').format(dashboardData.total_collected || 0)}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <BasicCard name={"ACTIVE SCHEDULES"} value={dashboardData.active_schedules || 0}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <BasicCard name={"ACTIVE USERS"} value={dashboardData.active_users || 0}/>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    )
}